import React, { useState, useEffect, useCallback } from 'react'
import {
  IconButton,
  Slide,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { TransitionProps } from '@mui/material/transitions'
import { Survey, SurveyResponse, SurveyModalProps } from '../../types/survey'
import {
  StyledDialog,
  CloseButtonContainer,
  StyledDialogContent,
  HeadingContainer,
  Title,
  Subtitle,
  EmojiContainer,
  EmojiButton,
  EmojiIcon,
  EmojiLabel,
  StyledTextArea,
  SubmitButton,
  TextAreaContainer,
  CharacterCounter,
} from './components'

// Re-export types for backward compatibility
export type { Survey, SurveyResponse, SurveyModalProps }

const MAX_LENGTH = 300

const Transition = React.forwardRef<
  unknown,
  TransitionProps & {
    children: React.ReactElement<any, any>
  }
>((props, ref) => <Slide direction="up" ref={ref} {...props} />)

Transition.displayName = 'SurveyModalTransition'

const emojiOptions = [
  { emoji: '😞', label: 'Negative' },
  { emoji: '😐', label: 'Neutral' },
  { emoji: '😊', label: 'Positive' },
]

export function SurveyModal({ open, question, isSubmitting, onSubmit, onClose }: SurveyModalProps) {
  const [response, setResponse] = useState<string | null>(null)
  const [comments, setComments] = useState('')
  
  const characterCount = comments.length

  useEffect(() => {
    if (comments.length > MAX_LENGTH) {
      setComments(comments.slice(0, MAX_LENGTH))
    }
  }, [comments])

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setResponse(null)
      setComments('')
    }
  }, [open])

  const handleSubmit = useCallback(() => {
    if (response === null) {
      return
    }
    
    onSubmit({
      response,
      comments,
    })
  }, [response, comments, onSubmit])

  const handleEmojiClick = useCallback((value: string) => {
    setResponse(currentResponse => (currentResponse === value ? null : value))
  }, [])

  const isFormValid = response !== null

  return (
    <StyledDialog
      open={open}
      TransitionComponent={Transition}
      data-testid="survey-modal"
    >
      <CloseButtonContainer>
        <IconButton
          onClick={onClose}
          size="small"
          data-testid="survey-modal-close"
        >
          <CloseIcon />
        </IconButton>
      </CloseButtonContainer>
      
      <StyledDialogContent>
        <HeadingContainer>
          <Title>We Value Your Feedback</Title>
          <Subtitle>
            {question}
          </Subtitle>
        </HeadingContainer>

        <EmojiContainer>
          {emojiOptions.map((option) => (
            <EmojiButton
              key={option.label}
              selected={response === option.label}
              onClick={() => handleEmojiClick(option.label)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleEmojiClick(option.label)
                }
              }}
              data-testid={`emoji-${option.label}`}
              tabIndex={0}
              role="button"
              aria-label={`Rate as ${option.label}`}
              aria-pressed={response === option.label}
            >
              <EmojiIcon aria-hidden="true">{option.emoji}</EmojiIcon>
              <EmojiLabel>{option.label}</EmojiLabel>
            </EmojiButton>
          ))}
        </EmojiContainer>

        <TextAreaContainer>
          <StyledTextArea
            fullWidth
            variant="outlined"
            placeholder="Like to add more detail?"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            data-testid="survey-comments-textarea"
            multiline
            rows={5}
            InputProps={{
              endAdornment: (
                <CharacterCounter aria-live="polite">
                  {characterCount}
                  /
                  {MAX_LENGTH}
                </CharacterCounter>
              ),
            }}
          />
        </TextAreaContainer>

        <SubmitButton
          variant="outlined"
          sx={{ padding: '10px', textTransform: 'none' }}
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          data-testid="survey-submit-button"
          aria-label={isSubmitting ? 'Submitting feedback' : 'Submit feedback'}
        >
          {isSubmitting ? 'Sending...' : 'Send feedback!'}
        </SubmitButton>
      </StyledDialogContent>
    </StyledDialog>
  )
}

export default SurveyModal